import { getMsalInstance } from "../auth/msalConfig";
import { ChatType } from "../types/chat";
import { ValidateableCvSections } from "../types/cvSections";
import { AiHttpService } from "./aiHttpService";

interface ChatMessage {
  text: string;
  isUser: boolean;
  citations?: string;
}

interface ChatHistoryItem {
  message: string;
  isUser: boolean;
  timestamp: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

interface ChatResponse {
  response: string;
  citations?: string;
  chatId?: string;
  agentName?: string;
  agentType?: string;
}

interface ChatRequest {
  Message: string;
  ChatType: ChatType;
  ChatId?: string;
}

export class ChatService extends AiHttpService {
  private static instance: ChatService;
  private chatHistory: ChatMessage[] = [];
  private currentChatId: string | null = null;

  private constructor() {
    super(); // No endpoint parameter needed anymore
  }

  // Uses inherited getAccessToken() from BaseService

  public static getInstance(): ChatService {
    if (!ChatService.instance) {
      ChatService.instance = new ChatService();
    }
    return ChatService.instance;
  }

  // Start a new chat session (clear current state)
  startNewChat(): void {
    this.currentChatId = null;
    this.chatHistory = [];
  }

  // Get current chat ID
  getCurrentChatId(): string | null {
    return this.currentChatId;
  }

  // Set chat ID for continuing existing conversation
  setChatId(chatId: string): void {
    this.currentChatId = chatId;
    // Clear local history since it will be managed by the backend
    this.chatHistory = [];
  }

  async sendChatMessage(
    message: string,
    chatType: ChatType
  ): Promise<ChatResponse> {
    try {
      // Add user message to local history for UI display
      this.chatHistory.push({ text: message, isUser: true });

      console.log(
        "[ChatService] Sending chat message to:",
        this.buildUrl("chat-with-assistant")
      );
      const request: ChatRequest = {
        Message: message,
        ChatType: chatType,
        ChatId: this.currentChatId || undefined, // Only send if we have one
      };

      const response = await this.post<ChatRequest, ChatResponse>(
        request,
        "/chat-with-assistant"
      );
      console.log("[ChatService] Received response:", response);

      if (!response || !response.response) {
        throw new Error("Invalid response format from chat service");
      }

      // Store the ChatId from the response if this is a new conversation
      if (response.chatId && !this.currentChatId) {
        this.currentChatId = response.chatId;
      }

      // Add AI response to local history for UI display
      this.chatHistory.push({
        text: response.response,
        isUser: false,
        citations: response.citations,
      });

      return response;
    } catch (error) {
      console.error("[ChatService] Error sending chat request:", error);
      if (error instanceof Error) {
        throw new Error(`Chat service error: ${error.message}`);
      }
      throw error;
    }
  }

  async* streamChatMessage(
    message: string,
    chatType: ChatType
  ): AsyncGenerator<ChatResponse, void, unknown> {
    try {
      // Add user message to local history for UI display
      this.chatHistory.push({ text: message, isUser: true });

      console.log(
        "[ChatService] Sending streaming chat message to:",
        this.buildUrl("stream-chat-with-assistant")
      );

      const request: ChatRequest = {
        Message: message,
        ChatType: chatType,
        ChatId: this.currentChatId || undefined,
      };

      const url = this.buildUrl("/stream-chat-with-assistant");
      const token = await this.getAccessToken();

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Response body is not readable");
      }

      let buffer = "";
      let fullResponse = "";
      let finalCitations = "";
      let chatId: string | undefined;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.trim()) {
            try {
              const chunk: ChatResponse = JSON.parse(line);
              console.log("[ChatService] Received chunk:", chunk);
              // Store chatId from first chunk
              if (chunk.chatId && !chatId) {
                chatId = chunk.chatId;
                if (!this.currentChatId) {
                  this.currentChatId = chatId;
                }
              }

              // Accumulate the full response
              if (chunk.response) {
                fullResponse += chunk.response;
              }

              // Store citations (usually comes in the last chunk)
              if (chunk.citations) {
                finalCitations = chunk.citations;
              }

              // Yield the chunk
              yield chunk;
            } catch (parseError) {
              console.error("[ChatService] Error parsing JSON chunk:", parseError, line);
            }
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim()) {
        try {
          const chunk: ChatResponse = JSON.parse(buffer);
          if (chunk.chatId && !chatId) {
            chatId = chunk.chatId;
          }
          if (chunk.response) {
            fullResponse += chunk.response;
          }
          if (chunk.citations) {
            finalCitations = chunk.citations;
          }
          yield chunk;
        } catch (parseError) {
          console.error("[ChatService] Error parsing final JSON chunk:", parseError);
        }
      }

      // Add complete AI response to local history
      this.chatHistory.push({
        text: fullResponse,
        isUser: false,
        citations: finalCitations,
      });

      console.log("[ChatService] Streaming completed. Total response length:", fullResponse.length);
    } catch (error) {
      console.error("[ChatService] Error in streaming chat:", error);
      if (error instanceof Error) {
        throw new Error(`Streaming chat service error: ${error.message}`);
      }
      throw error;
    }
  }

  // Method to clear chat history and start new conversation
  clearChatHistory(): void {
    this.chatHistory = [];
    this.currentChatId = null;
  }

  // Method to get current chat history
  getChatHistory(): ChatMessage[] {
    return [...this.chatHistory];
  }

  // Method to load chat history from backend
  async loadChatHistory(chatId: string): Promise<ChatMessage[]> {
    try {
      console.log("[ChatService] Loading chat history for chatId:", chatId);

      const response = await this.get<ChatHistoryItem[]>(`/chat-history/${chatId}`);

      if (response && Array.isArray(response)) {
        // Clear current history and load from backend
        this.chatHistory = response.map(item => ({
          text: item.message,
          isUser: item.isUser
        }));

        // Set the current chat ID
        this.currentChatId = chatId;

        console.log("[ChatService] Loaded chat history:", this.chatHistory.length, "messages");
        return [...this.chatHistory];
      }

      return [];
    } catch (error) {
      console.error("[ChatService] Error loading chat history:", error);
      return [];
    }
  }

  async sendImprovementRequest(textToImprove: string, chatId?: string): Promise<any> {
    try {
      console.log("[ChatService] Sending improvement request");
      const request = {
        TextToImprove: textToImprove,
        ChatId: chatId // Let backend generate if not provided
      };
      const response = await this.post(request, "/improve-text");
      console.log("[ChatService] Received improvement response:", response);
      return response;
    } catch (error) {
      console.error(
        "[ChatService] Error sending text improvement request:",
        error
      );
      if (error instanceof Error) {
        throw new Error(`Improvement service error: ${error.message}`);
      }
      throw error;
    }
  }

  async sendValidationRequest(
    cvId: number,
    section: ValidateableCvSections,
    sectionData: string,
    chatId?: string
  ): Promise<any> {
    try {
      console.log("[ChatService] Sending validation request for CV:", cvId, "Section:", section);

      // Get user email from MSAL
      const msalInstance = await getMsalInstance();
      const account = msalInstance.getAllAccounts()[0];

      if (!account || !account.username) {
        throw new Error("No authenticated user found");
      }

      const request = {
        CvId: cvId,
        CvSection: section,
        SectionData: sectionData,
        UserEmail: account.username, // Add user email as required by backend
        ChatId: chatId // Let backend generate if not provided
      };
      const response = await this.post(request, "/validate-cv");
      console.log("[ChatService] Received validation response:", response);
      return response;
    } catch (error) {
      console.error("[ChatService] Error sending validation request:", error);
      if (error instanceof Error) {
        throw new Error(`Validation service error: ${error.message}`);
      }
      throw error;
    }
  }

  async generateSummary(
    userId: number,
    profile?: string,
    chatId?: string
  ): Promise<{ response: string }> {
    try {
      console.log("[ChatService] Generating summary for user:", userId);
      const request = {
        CvId: userId,
        Profile: profile,
        ChatId: chatId // Let backend generate if not provided
      };
      const response: any = await this.post(request, "/generate-summary");
      if (!response || typeof response.response !== "string") {
        throw new Error("Invalid response from generate-summary");
      }
      return { response: response.response };
    } catch (error) {
      console.error("[ChatService] Error generating summary:", error);
      if (error instanceof Error) {
        throw new Error(`Generate summary service error: ${error.message}`);
      }
      throw error;
    }
  }

  async suggestSimilarSkills(skillName: string, chatId?: string): Promise<string[]> {
    try {
      console.log("[ChatService] Suggesting similar skills for:", skillName);
      const request = {
        Skill: skillName,
        ChatId: chatId // Let backend generate if not provided
      };
      const response: any = await this.post(request, "/suggest-similar-skills");
      if (!response || typeof response.response !== "string") {
        throw new Error("Invalid response from suggest-similar-skills");
      }
      // Split the comma-separated string into an array and trim whitespace
      return response.response.split(",").map((skill: string) => skill.trim());
    } catch (error) {
      console.error("[ChatService] Error suggesting similar skills:", error);
      if (error instanceof Error) {
        throw new Error(
          `Suggest similar skills service error: ${error.message}`
        );
      }
      throw error;
    }
  }
}
